import { useRef, useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { fadeUp, stagger, viewport } from '../../utils/animations';
import { RiDoubleQuotesL } from 'react-icons/ri';
import { HiChevronLeft, HiChevronRight } from 'react-icons/hi2';
import blue from '../../images/blue.webp';
import kris from '../../images/kris.webp';
import warren from '../../images/warren.webp';
const REVIEWS = [
  {
    name: 'Blue Larem',
    title: 'Web Developer at EXEChain',
    location: 'Turkey',
    avatar: blue,
    rating: 5,
    text: "Is a constructive communicator and is capable of discussing difficult issues effectively and to the point",
    color: '#6366f1',
  },
  {
    name: 'Kristopher Scott',
    title: 'Executive Manager at EXEChain',
    location: 'United States',
    avatar: kris,
    rating: 5,
    text: "I wanted to recognize ujjal zaman for all his professional expertise and hard work that he put forth for Exechain. <br/> His work ethic was remarkable; he exceed our expectations!",
    color: '#8b5cf6',
    linkedin: 'https://www.linkedin.com/in/ujjal-zaman',
  },
  {
    name: 'Exe Warren',
    title: 'CEO at EXEChain',
    location: 'United States',
    avatar: warren,
    rating: 5,
    text: "Positively contributes to the overall performance of the company through consistent and high-quality work.",
    color: '#f59e0b',
    linkedin: 'https://www.linkedin.com/in/ujjal-zaman',
  }
];

function Stars({ color }) {
  return (
    <div className="flex gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <motion.span
          key={i}
          initial={{ opacity: 0, scale: 0 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ delay: i * 0.07, type: 'spring', stiffness: 300 }}
          style={{ color: color || '#f59e0b' }}
          className="text-sm"
        >★</motion.span>
      ))}
    </div>
  );
}

export default function Testimonial() {
  const scrollRef = useRef(null);
  const [canLeft,  setCanLeft]  = useState(false);
  const [canRight, setCanRight] = useState(true);
  const [activeIdx, setActiveIdx] = useState(0);

  const updateState = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 4);
    setCanRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 4);
    const idx = Math.round(el.scrollLeft / (el.clientWidth * 0.82));
    setActiveIdx(Math.min(idx, REVIEWS.length - 1));
  };

  const scroll = (dir) => {
    const el = scrollRef.current;
    if (!el) return;
    const cardW = el.querySelector('[data-card]')?.offsetWidth || 380;
    el.scrollBy({ left: dir * (cardW + 20), behavior: 'smooth' });
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateState();
  }, []);

  return (
    <section id="testimonials" className="relative py-28 lg:py-36 overflow-hidden">
      <div className="absolute inset-0 pointer-events-none"
        style={{ background: 'radial-gradient(ellipse 70% 60% at 50% 100%, rgba(99,102,241,0.04) 0%, transparent 70%)' }} />

      <div className="max-w-7xl mx-auto px-6 sm:px-8">

        {/* Header */}
        <motion.div variants={stagger(0.12)} initial="hidden" whileInView="show" viewport={viewport}
          className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-5 mb-12"
        >
          <div>
            <motion.div variants={fadeUp} className="section-label">
              <span className="font-mono text-xs text-indigo-400 font-semibold uppercase tracking-[0.18em]">06 / Testimonials</span>
            </motion.div>
            <motion.h2 variants={fadeUp} className="font-display font-extrabold text-4xl sm:text-5xl text-white tracking-tight">
              What clients <span className="gradient-text">say</span>
            </motion.h2>
          </div>

          {/* Arrows */}
          <motion.div variants={fadeUp} className="flex items-center gap-2">
            <button
              onClick={() => scroll(-1)}
              disabled={!canLeft}
              className={`p-3 rounded-2xl border transition-all duration-200 ${
                canLeft
                  ? 'glass border-white/[0.1] text-white hover:border-indigo-500/40 hover:bg-indigo-500/10'
                  : 'border-white/[0.04] text-zinc-800 cursor-not-allowed'
              }`}
            >
              <HiChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={() => scroll(1)}
              disabled={!canRight}
              className={`p-3 rounded-2xl border transition-all duration-200 ${
                canRight
                  ? 'glass border-white/[0.1] text-white hover:border-indigo-500/40 hover:bg-indigo-500/10'
                  : 'border-white/[0.04] text-zinc-800 cursor-not-allowed'
              }`}
            >
              <HiChevronRight className="w-5 h-5" />
            </button>
          </motion.div>
        </motion.div>

        {/* Horizontal scroll container */}
        <div
          ref={scrollRef}
          onScroll={updateState}
          className="flex gap-5 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-hide"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {REVIEWS.map((r, i) => (
            <motion.div
              key={r.name}
              data-card
              initial={{ opacity: 0, x: 32 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: '-40px' }}
              transition={{ delay: i * 0.08, duration: 0.55 }}
              whileHover={{ y: -6 }}
              className="group relative flex-shrink-0 w-[80vw] sm:w-[380px] lg:w-[400px] snap-start
                         glass glass-hover rounded-3xl border border-white/[0.06] overflow-hidden"
            >
              {/* Top color line */}
              <div className="absolute top-0 left-0 right-0 h-[2px]"
                style={{ background: `linear-gradient(90deg, transparent, ${r.color}, transparent)` }} />

              <div className="p-6 sm:p-7 flex flex-col h-full min-h-[280px]">
                {/* Quote icon + stars */}
                <div className="flex items-start justify-between mb-5">
                  <div
                    className="w-10 h-10 flex items-center justify-center rounded-2xl"
                    style={{ background: `${r.color}15`, border: `1px solid ${r.color}25` }}
                  >
                    <RiDoubleQuotesL className="w-4.5 h-4.5" style={{ color: r.color }} />
                  </div>
                  <Stars color={r.color} />
                </div>

                {/* Text */}
                <p className="text-sm text-zinc-400 leading-relaxed flex-1">"{r.text}"</p>

                {/* Author */}
                <div className="flex items-center gap-3 mt-6 pt-5 border-t border-white/[0.05]">
                  <img
                    src={r.avatar} alt={r.name}
                    className="w-10 h-10 rounded-full object-cover border border-white/[0.08]"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-display font-semibold text-sm text-white truncate">{r.name}</p>
                    <p className="text-xs text-zinc-600 truncate">{r.title}</p>
                  </div>
                  <span className="font-mono text-[10px] text-zinc-700 flex-shrink-0">{r.location}</span>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Dot indicators */}
        <div className="flex justify-center gap-2 mt-5">
          {REVIEWS.map((_, i) => (
            <button
              key={i}
              onClick={() => {
                const el = scrollRef.current;
                const card = el?.querySelector('[data-card]');
                if (!el || !card) return;
                el.scrollTo({ left: i * (card.offsetWidth + 20), behavior: 'smooth' });
              }}
              className={`rounded-full transition-all duration-300 ${
                i === activeIdx
                  ? 'w-5 h-1.5 bg-indigo-500'
                  : 'w-1.5 h-1.5 bg-zinc-800 hover:bg-zinc-600'
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
